import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, PhoneIcon, ArrowLeftIcon, ArrowRightIcon, XIcon, UserIcon, CameraIcon, UploadCloudIcon, ShieldCheckIcon, CheckCircle2Icon, ChevronDownIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { Logo } from './Logo';
type DialCode = {
  iso: string;
  dial: string;
  name: string;
};
// UAE first, then other GCC markets.
const DIAL_CODES: DialCode[] = [{
  iso: 'ae',
  dial: '+971',
  name: 'United Arab Emirates'
}, {
  iso: 'sa',
  dial: '+966',
  name: 'Saudi Arabia'
}, {
  iso: 'qa',
  dial: '+974',
  name: 'Qatar'
}, {
  iso: 'kw',
  dial: '+965',
  name: 'Kuwait'
}, {
  iso: 'bh',
  dial: '+973',
  name: 'Bahrain'
}, {
  iso: 'om',
  dial: '+968',
  name: 'Oman'
}];
function CountryFlag({
  iso
}: {
  iso: string;
}) {
  return <img src={`https://flagcdn.com/w40/${iso}.png`} srcSet={`https://flagcdn.com/w80/${iso}.png 2x`} width={20} height={15} alt="" aria-hidden="true" className="rounded-[3px] object-cover shadow-sm ring-1 ring-black/5" data-id="element-2772" data-mp="2482ko28fzws9" />;
}
// Phone input with a separated, selectable country dial-code prefix.
function PhoneField({
  value,
  onChange,
  dialCode,
  onDialCodeChange
}: {
  value: string;
  onChange: (v: string) => void;
  dialCode: DialCode;
  onDialCodeChange: (d: DialCode) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return <div className="flex items-stretch gap-2" data-id="element-2773" data-mp="3sjcii5eoi4ia">
      {/* Dial-code prefix */}
      <div className="relative" ref={ref} data-id="element-2774" data-mp="3slfini9jpe0q">
        <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={`h-full flex items-center gap-2 px-3 border rounded-xl transition-colors ${open ? 'border-primary-indigo bg-primary-indigo/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-2775" data-mp="3iv77aamhtf7f">
          <CountryFlag iso={dialCode.iso} data-id="element-2776" data-mp="3dyq9spteloho" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-2777" data-mp="5gkci151w8p5">
            {dialCode.dial}
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180 text-primary-indigo' : ''}`} data-id="element-2778" data-mp="aevlk9niuf6b" />
        </button>
        <AnimatePresence data-id="element-2779" data-mp="pu8osc9wdwk">
          {open && <motion.div role="listbox" initial={{
          opacity: 0,
          y: -6,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -6,
          scale: 0.97
        }} transition={{
          duration: 0.15
        }} className="absolute left-0 mt-2 w-64 bg-white border border-slate-100 rounded-2xl shadow-[0_16px_48px_-8px_rgb(0,0,0,0.18)] overflow-hidden z-50 p-1.5 space-y-0.5" data-id="element-2780" data-mp="oj4wsitv65g">
              {DIAL_CODES.map(d => {
            const active = d.iso === dialCode.iso;
            return <button key={d.iso} type="button" role="option" aria-selected={active} onClick={() => {
              onDialCodeChange(d);
              setOpen(false);
            }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? 'bg-primary-indigo/[0.06]' : 'hover:bg-slate-50'}`} data-id="element-2781" data-mp="1q0pi5chgtyx1">
                    <CountryFlag iso={d.iso} data-id="element-2782" data-mp="ait8wfduj92c" />
                    <span className="flex-1 min-w-0" data-id="element-2783" data-mp="ei1zjbdlaqto">
                      <span className={`block text-sm truncate ${active ? 'font-semibold text-primary-indigo' : 'font-medium text-slate-800'}`} data-id="element-2784" data-mp="eko6b8snp2au">
                        {d.name}
                      </span>
                    </span>
                    <span className={`text-sm font-semibold ${active ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2785" data-mp="tfcit6wh50zy">
                      {d.dial}
                    </span>
                    {active && <CheckIcon className="w-4 h-4 text-primary-indigo shrink-0" data-id="element-2786" data-mp="176zh1c9z8jld" />}
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>
      {/* Local number */}
      <input type="tel" value={value} onChange={e => onChange(e.target.value)} placeholder="50 123 4567" className="flex-1 min-w-0 px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2787" data-mp="1h5osam7db5sn" />
    </div>;
}
type Step = 'method' | 'email' | 'phone' | 'otp' | 'profile' | 'verify';
interface LoginProps {
  onLogin: () => void;
  onClose?: () => void;
}
function GoogleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" data-id="element-2788" data-mp="3kdfkxrmizymw">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" data-id="element-2789" data-mp="3fem5htnohxk3" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" data-id="element-2790" data-mp="35p2neq7si7qa" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" data-id="element-2791" data-mp="715hun0n8guv" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" data-id="element-2792" data-mp="1wgg6aob5z5mm" />
    </svg>;
}
function AppleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-2793" data-mp="1mplivgm6lzzl">
      <path d="M16.36 12.78c-.02-2.05 1.67-3.03 1.75-3.08-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.62-.72-2.67-.7-1.37.02-2.64.8-3.35 2.03-1.43 2.48-.37 6.15 1.02 8.16.68.98 1.49 2.08 2.55 2.04 1.02-.04 1.41-.66 2.65-.66 1.23 0 1.58.66 2.66.64 1.1-.02 1.79-1 2.46-1.99.78-1.14 1.1-2.24 1.12-2.3-.02-.01-2.15-.83-2.18-3.27zM14.3 6.58c.56-.68.94-1.62.84-2.58-.81.03-1.79.54-2.37 1.22-.52.6-.98 1.56-.86 2.48.9.07 1.83-.46 2.39-1.12z" data-id="element-2794" data-mp="1rnn15zbs9va0" />
    </svg>;
}
export function Login({
  onLogin,
  onClose
}: LoginProps) {
  const [step, setStep] = useState<Step>('method');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState<DialCode>(DIAL_CODES[0]);
  const [fullName, setFullName] = useState('');
  const [idStatus, setIdStatus] = useState<'idle' | 'uploaded'>('idle');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const goToOtp = () => setStep('otp');
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const otpComplete = otp.every(d => d !== '');
  const stepVariants = {
    enter: {
      opacity: 0,
      x: 24
    },
    center: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -24
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-id="element-2795" data-mp="2v5uqrhs4goor">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-2796" data-mp="34vo3im4039ul" />
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="relative w-full max-w-md bg-white rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.18)] border border-slate-100 p-8 z-10" data-id="element-2797" data-mp="39w1yp3ki6pb7">
        {onClose && <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-full transition-colors" data-id="element-2798" data-mp="2q5qa9rr7rtyj">
            <XIcon className="w-5 h-5" data-id="element-2799" data-mp="5epsti8sx6gb" />
          </button>}
        <div className="flex justify-center mb-6" data-id="element-2800" data-mp="1pxs4gcslke42">
          <Logo size="md" data-id="element-2801" data-mp="1pxa3j2uzpbpt" />
        </div>

        <AnimatePresence mode="wait" data-id="element-2802" data-mp="1lw6m4bxan7mh">
          {/* METHOD SELECTION */}
          {step === 'method' && <motion.div key="method" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2803" data-mp="1h585tccb29j8">
              <div className="text-center mb-7" data-id="element-2804" data-mp="177aeuh329ymx">
                <h1 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-2805" data-mp="1783jncigme2f">
                  Welcome to Talab
                </h1>
                <p className="text-slate-500 text-sm" data-id="element-2806" data-mp="y6m2rrx8a8co">
                  Sign in to let your AI agent find anything for you.
                </p>
              </div>

              <div className="space-y-3" data-id="element-2807" data-mp="t93sny7h5n33">
                <button onClick={() => setStep('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2808" data-mp="2skd9ojo6ae4f">
                  <GoogleMark data-id="element-2809" data-mp="i01wpjvffzgu" />
                  Continue with Google
                </button>
                <button onClick={() => setStep('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 bg-dark-navy text-white rounded-xl font-medium hover:opacity-90 transition-opacity" data-id="element-2810" data-mp="d0ysg1n9tx9x">
                  <AppleMark data-id="element-2811" data-mp="3ugdvp25rh4nf" />
                  Continue with Apple
                </button>

                <div className="flex items-center gap-3 py-2" data-id="element-2812" data-mp="3qek1ncq2myw8">
                  <div className="flex-1 h-px bg-slate-100" data-id="element-2813" data-mp="7zrko2l4oy39" />
                  <span className="text-xs text-slate-400 font-medium" data-id="element-2814" data-mp="81ccvljz04xy">OR</span>
                  <div className="flex-1 h-px bg-slate-100" data-id="element-2815" data-mp="37zzk7kqrkki" />
                </div>

                <button onClick={() => setStep('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2816" data-mp="3bo2zts9iy6yt">
                  <PhoneIcon className="w-5 h-5 text-primary-indigo" data-id="element-2817" data-mp="1qyfrf0pr9qny" />
                  Continue with Phone
                </button>
                <button onClick={() => setStep('email')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2818" data-mp="1d83w6sxvjbsl">
                  <MailIcon className="w-5 h-5 text-primary-indigo" data-id="element-2819" data-mp="29x1u2k0x7ptz" />
                  Continue with Email
                </button>
              </div>

              <p className="text-center text-xs text-slate-400 mt-6 leading-relaxed" data-id="element-2820" data-mp="1vxaiepxvmlj5">
                By continuing you agree to Talab's Terms of Service and Privacy
                Policy.
              </p>
            </motion.div>}

          {/* EMAIL STEP */}
          {step === 'email' && <motion.div key="email" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2821" data-mp="pfdkmktvuuiu">
              <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2822" data-mp="1ek05iwyx6hz">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2823" data-mp="3nr8pbp3n27ek" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2824" data-mp="3is5n0cf1mfaa">
                Sign in with email
              </h1>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2825" data-mp="39t2170ohrls3">
                We'll also verify your phone number to secure your account.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2826" data-mp="34wbmagofaw89">
                Email address
              </label>
              <div className="relative mb-4" data-id="element-2827" data-mp="133doe6fshdgg">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2828" data-mp="134f9l3qirgbw" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2829" data-mp="1am3plaog624o" />
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2830" data-mp="rp2kzqyt2419">
                Phone number <span className="text-error" data-id="element-2831" data-mp="wnx0aixkdagm">*</span>
              </label>
              <div className="mb-6" data-id="element-2832" data-mp="11lfdwmmj0duy">
                <PhoneField value={phone} onChange={setPhone} dialCode={dialCode} onDialCodeChange={setDialCode} data-id="element-2833" data-mp="11kdp7fcl7sur" />
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1" data-id="element-2834" data-mp="2d0kg7piqwae1">
                  <MessageCircleIcon className="w-3.5 h-3.5 text-[#25D366]" data-id="element-2835" data-mp="2h1e4wl40ht7z" />
                  Required — we'll send a one-time code via WhatsApp to verify
                  it.
                </p>
              </div>

              <button onClick={goToOtp} disabled={!email.includes('@') || phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2836" data-mp="2ao9xz8jpzwk5">
                Send verification code
                <ArrowRightIcon className="w-4 h-4" data-id="element-2837" data-mp="38ah2kig0xowi" />
              </button>
            </motion.div>}

          {/* PHONE STEP */}
          {step === 'phone' && <motion.div key="phone" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2838" data-mp="3ham9krhb2la5">
              <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2839" data-mp="3vxkxicgyexk4">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2840" data-mp="3mx67x0wyiwat" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2841" data-mp="3rvr1wilm8jqt">
                Verify your phone
              </h1>
              <p className="text-slate-500 text-sm mb-6 flex items-center gap-1.5" data-id="element-2842" data-mp="3d6bb52m34eb4">
                <MessageCircleIcon className="w-4 h-4 text-[#25D366] shrink-0" data-id="element-2843" data-mp="3hy32hfd2eo2b" />
                Enter your phone number and we'll send a one-time code via
                WhatsApp.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2844" data-mp="3878ea5g9dee8">
                Phone number <span className="text-error" data-id="element-2845" data-mp="2u7z2dhz356g2">*</span>
              </label>
              <div className="mb-6" data-id="element-2846" data-mp="2z727t3iz9eok">
                <PhoneField value={phone} onChange={setPhone} dialCode={dialCode} onDialCodeChange={setDialCode} data-id="element-2847" data-mp="2z6adrx513p0e" />
              </div>

              <button onClick={goToOtp} disabled={phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2848" data-mp="zz3m66dkejxk">
                Send verification code
                <ArrowRightIcon className="w-4 h-4" data-id="element-2849" data-mp="1jp5kzvpq1a52" />
              </button>
            </motion.div>}

          {/* OTP STEP */}
          {step === 'otp' && <motion.div key="otp" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2850" data-mp="19p52ud3l902g">
              <button onClick={() => setStep(email ? 'email' : 'phone')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2851" data-mp="3ug5qj4clgjt4">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2852" data-mp="3pbuyfg29k524" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2853" data-mp="3u93l9tycwx9p">
                Enter verification code
              </h1>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-[#25D366]/10 text-[#1da851] text-xs font-medium" data-id="element-2854" data-mp="6xlyzlj26qmn">
                <MessageCircleIcon className="w-3.5 h-3.5" data-id="element-2855" data-mp="bvdqiefskaqb" />
                Sent via WhatsApp
              </div>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2856" data-mp="gwjlyk0u6c3j">
                We sent a 6-digit code on WhatsApp to{' '}
                <span className="font-medium text-slate-700" data-id="element-2857" data-mp="lsihqsohuwdz">
                  {phone ? `${dialCode.dial} ${phone}` : 'your phone'}
                </span>
                .
              </p>

              <div className="flex justify-between gap-2 mb-6" data-id="element-2858" data-mp="2noc31xbcnlfz">
                {otp.map((digit, i) => <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} aria-label={`Digit ${i + 1}`} className="w-12 h-14 text-center text-xl font-bold border border-slate-200 rounded-xl outline-none focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/20 transition-all" data-id="element-2859" data-mp="tc15tdclhftf" data-mpi={i} />)}
              </div>

              <button onClick={() => setStep('profile')} disabled={!otpComplete} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2860" data-mp="fgz28wv802js">
                Verify & continue
              </button>

              <p className="text-center text-sm text-slate-500 mt-5" data-id="element-2861" data-mp="23dos2fx9wufy">
                Didn't get the code?{' '}
                <button className="font-medium text-primary-indigo hover:underline" data-id="element-2862" data-mp="1oh7j1vck3rwk">
                  Resend
                </button>
              </p>
            </motion.div>}

          {/* PROFILE COMPLETION STEP */}
          {step === 'profile' && <motion.div key="profile" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2863" data-mp="2m1jzli2alc0m">
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2864" data-mp="2cc0gcbao3g58">
                Confirm your details
              </h1>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2865" data-mp="300jp0rxmpuxu">
                Let's make sure we have your name and email right.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2866" data-mp="108jiq74rzhmj">
                Full name <span className="text-error" data-id="element-2867" data-mp="158ny1tty66b6">*</span>
              </label>
              <div className="relative mb-4" data-id="element-2868" data-mp="159z460av7q5g">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2869" data-mp="qi8dmh9cv2e2" />
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Ahmed Al Mansoori" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2870" data-mp="vh1py6cwoncn" />
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2871" data-mp="ckjsr9fafv90">
                Email address <span className="text-error" data-id="element-2872" data-mp="21zgguz89mcgn">*</span>
              </label>
              <div className="relative mb-6" data-id="element-2873" data-mp="26y1hv0vcd065">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2874" data-mp="26wqdozxzhqei" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2875" data-mp="3rfttv1smyrl5" />
              </div>

              <button onClick={() => setStep('verify')} disabled={fullName.trim().length < 2 || !email.includes('@')} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2876" data-mp="dz2wcdp16s46">
                Continue
                <ArrowRightIcon className="w-4 h-4" data-id="element-2877" data-mp="2psaxbfpzk1xf" />
              </button>
            </motion.div>}

          {/* ID VERIFICATION STEP */}
          {step === 'verify' && <motion.div key="verify" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2878" data-mp="3agu2qcnlei6m">
              <div className="flex flex-col items-center text-center mb-6" data-id="element-2879" data-mp="3jecojj9290jk">
                <div className="w-14 h-14 rounded-2xl bg-primary-indigo/10 flex items-center justify-center mb-4" data-id="element-2880" data-mp="3ofaeii0bayvm">
                  <ShieldCheckIcon className="w-7 h-7 text-primary-indigo" data-id="element-2881" data-mp="3tdc13cwy5gbu" />
                </div>
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2882" data-mp="18zywqgtj9ku">
                  Verify your identity
                </h1>
                <p className="text-slate-500 text-sm" data-id="element-2883" data-mp="63dqehsd0wk8">
                  Scan or upload your ID. We rely on a verified ID to keep Talab
                  safe and unlock full access.
                </p>
              </div>

              {idStatus === 'uploaded' ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl mb-6" data-id="element-2884" data-mp="kytpj17srinh">
                  <CheckCircle2Icon className="w-6 h-6 text-success shrink-0" data-id="element-2885" data-mp="25e9l4tezf7rj" />
                  <div className="text-left" data-id="element-2886" data-mp="20folow7k0s45">
                    <p className="font-medium text-slate-900 text-sm" data-id="element-2887" data-mp="1vhwr1ufiyrw9">
                      ID submitted
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2888" data-mp="1qk50p4uj3du6">
                      We'll review it shortly — you can continue in the
                      meantime.
                    </p>
                  </div>
                </div> : <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-2889" data-mp="2ja3m8q9iksu3">
                  <button onClick={() => setIdStatus('uploaded')} className="flex flex-col items-center justify-center p-5 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-2890" data-mp="2efg3l816ysnf">
                    <CameraIcon className="w-7 h-7 text-slate-400 group-hover:text-primary-indigo mb-2 transition-colors" data-id="element-2891" data-mp="324ikuerwyjhe" />
                    <span className="font-medium text-sm text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-2892" data-mp="2rnoo91ihyj0m">
                      Scan ID
                    </span>
                  </button>
                  <button onClick={() => setIdStatus('uploaded')} className="flex flex-col items-center justify-center p-5 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-2893" data-mp="2hp8xizndtbrn">
                    <UploadCloudIcon className="w-7 h-7 text-slate-400 group-hover:text-primary-indigo mb-2 transition-colors" data-id="element-2894" data-mp="2vq1t7npf6cwc" />
                    <span className="font-medium text-sm text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-2895" data-mp="30ms847phn2g6">
                      Upload ID
                    </span>
                  </button>
                </div>}

              <button onClick={onLogin} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors" data-id="element-2896" data-mp="1hpfa7nt5y7tg">
                {idStatus === 'uploaded' ? 'Finish & continue' : 'Continue'}
              </button>
              {idStatus !== 'uploaded' && <button onClick={onLogin} className="w-full mt-2 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors" data-id="element-2897" data-mp="1mh70rycbkdjl">
                  Verify later
                </button>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
}